// SPDX-License-Identifier: AGPL-3.0-or-later

import {msg} from '@lingui/core/macro';

// Every new shop and Plus sales text, once. Shop components import from here and never
// write the same text a second time. The labels ("Owned", "Equip", "Equipped", "Included in Plus") and the
// AZN note keep their Turkish wording exactly.

export const SHOP_DESCRIPTOR = msg({
	message: 'Shop',
	comment: 'Direct messages list entry and page title for the shop where profile decorations and Plus are bought.',
});
export const ITEM_SALES_PAUSED_NOTICE_DESCRIPTOR = msg({
	message: 'Sales are paused right now. Items you own can still be equipped.',
	comment: 'Notice on the shop page while item sales are switched off.',
});
export const REDEEM_A_CODE_DESCRIPTOR = msg({
	message: 'Redeem a code',
	comment:
		'Heading of the code field on the shop page, and the button at the top of the Plus and shop pages that opens the code dialog.',
});
export const PASTE_A_CODE_OR_GIFT_LINK_DESCRIPTOR = msg({
	message: 'Paste a code or gift link',
	comment: 'Placeholder of the code field on the shop page.',
});
export const NOTHING_ON_SALE_DESCRIPTOR = msg({
	message: 'Nothing on sale right now',
	comment: 'Title of the empty shop page when no item is on sale.',
});
export const NEW_ITEMS_SHOW_UP_HERE_DESCRIPTOR = msg({
	message: 'New items show up here when they go on sale.',
	comment: 'Description of the empty shop page when no item is on sale.',
});
export const OWNED_DESCRIPTOR = msg({
	message: 'Owned',
	context: 'shop-item',
	comment: 'Chip on a shop card and in the item dialog for an item the user already has.',
});
export const INCLUDED_IN_PREMIUM_DESCRIPTOR = msg({
	message: 'Included in Plus',
	comment: 'Chip and price line of a shop item that every Plus member can wear.',
});
export const EQUIP_DESCRIPTOR = msg({
	message: 'Equip',
	comment: 'Button on a shop item the user has but does not wear.',
});
export const EQUIPPED_BUTTON_DESCRIPTOR = msg({
	message: 'Equipped',
	context: 'shop-button',
	comment: 'Disabled button on a shop item the user wears.',
});
export const EQUIP_ITEM_DESCRIPTOR = msg({
	message: 'Equip {name}',
	comment: 'Accessible label of the equip button on a shop card. {name} is the item name.',
});
export const BUY_ITEM_DESCRIPTOR = msg({
	message: 'Buy {name}',
	comment: 'Accessible label of the buy button on a shop card, and title of the buy dialog. {name} is the item name.',
});
export const NOT_ON_SALE_DESCRIPTOR = msg({
	message: 'Not on sale',
	comment: 'Price line of a shop item while item sales are switched off.',
});
export const PREMIUM_PRICE_DESCRIPTOR = msg({
	message: 'Plus: {price}',
	comment: 'Price line with what a Plus member pays for a shop item. {price} is a formatted price in TL or AZN.',
});
export const WAS_PRICE_DESCRIPTOR = msg({
	message: 'Was {price}',
	comment:
		'Hidden text for screen readers before a struck-through old price in the shop. {price} is a formatted price.',
});
export const DISCOUNT_PERCENT_DESCRIPTOR = msg({
	message: 'Discount {percent}',
	comment:
		'Hidden text for screen readers before a sale badge in the shop. {percent} is a formatted discount like -20%.',
});
export const DAYS_HOURS_LEFT_DESCRIPTOR = msg({
	message: '{days}d {hours}h left',
	comment: 'Sale countdown in the shop when a day or more is left. d is days, h is hours.',
});
export const HOURS_MINUTES_LEFT_DESCRIPTOR = msg({
	message: '{hours}h {minutes}m left',
	comment: 'Sale countdown in the shop when an hour or more is left. h is hours, m is minutes.',
});
export const MINUTES_LEFT_DESCRIPTOR = msg({
	message: '{minutes}m left',
	comment: 'Sale countdown in the shop when less than an hour is left. m is minutes.',
});
export const LESS_THAN_A_MINUTE_LEFT_DESCRIPTOR = msg({
	message: 'Less than a minute left',
	comment: 'Sale countdown in the shop in the last minute.',
});
export const SALE_ENDS_DESCRIPTOR = msg({
	message: 'Sale ends {date}',
	comment: 'Accessible label of the sale countdown in the shop. {date} is the date and time the sale ends.',
});
export const PROFILE_CARD_DESCRIPTOR = msg({
	message: 'Profile card',
	comment: 'Label of the profile card preview in the shop item dialog.',
});
export const PREVIEW_TAKES_THE_PLACE_DESCRIPTOR = msg({
	message: 'The preview takes the place of the {type} you wear.',
	comment:
		'Note in the shop item dialog when the user wears an item of the same type. {type} is a decoration type name such as Nameplate.',
});
export const PREVIEW_FIRST_MESSAGE_DESCRIPTOR = msg({
	message: 'Hey, look at my new look!',
	comment: 'First sample chat message in the shop item preview, written by the user.',
});
export const PREVIEW_SECOND_MESSAGE_DESCRIPTOR = msg({
	message: 'Nice, right?',
	comment: 'Second sample chat message in the shop item preview, written by the user.',
});
export const BUY_PREMIUM_DESCRIPTOR = msg({
	message: 'Buy {premiumProductName}',
	comment: 'Title of the buy dialog for a Plus package, and the button on the Plus page that opens the plans.',
});
export const PACKAGE_MONTHS_DESCRIPTOR = msg({
	message: '{months, plural, one {# month} other {# months}}',
	comment: 'Name of a Plus package in the package picker and on the package card.',
});
export const PRICE_DESCRIPTOR = msg({
	message: 'Price',
	comment: 'Row of the order summary in the buy dialog: the list price.',
});
export const SALE_PERCENT_DESCRIPTOR = msg({
	message: 'Sale ({percent})',
	comment: 'Row of the order summary in the buy dialog: the campaign discount. {percent} is a formatted discount.',
});
export const PREMIUM_DISCOUNT_PERCENT_DESCRIPTOR = msg({
	message: '{premiumProductName} discount ({percent})',
	comment: 'Row of the order summary in the buy dialog: the Plus member discount. {percent} is a formatted discount.',
});
export const TOTAL_DESCRIPTOR = msg({
	message: 'Total',
	comment: 'Row of the order summary in the buy dialog: what the user pays.',
});
export const ONE_TIME_PAYMENT_DESCRIPTOR = msg({
	message: 'One-time payment. It does not renew.',
	comment: 'Note under the Plus packages and in the Plus buy dialog.',
});
export const TIME_ADDED_ON_TOP_DESCRIPTOR = msg({
	message: 'Time is added on top of what you have left.',
	comment: 'Note for Plus members buying more Plus time.',
});
export const CLAIM_ACCOUNT_TO_BUY_DESCRIPTOR = msg({
	message: 'Claim your account to buy.',
	comment: 'Tooltip on a disabled buy button for an account that is not claimed yet.',
});
export const VERIFY_EMAIL_TO_BUY_DESCRIPTOR = msg({
	message: 'Verify your email to buy.',
	comment: 'Tooltip on a disabled buy button for an account whose email is not verified.',
});
export const LOADING_PRICE_DESCRIPTOR = msg({
	message: 'Loading price',
	comment: 'Accessible label of the order summary while the buy dialog asks the server for the price.',
});
export const PRICE_CHANGED_DESCRIPTOR = msg({
	message: 'The price changed',
	comment: 'Title in the buy dialog when the order price differs from the price shown.',
});
export const CONTINUE_AT_NEW_PRICE_DESCRIPTOR = msg({
	message: 'Continue at the new price',
	comment: 'Button in the buy dialog after the price changed.',
});
export const FINISH_PAYING_IN_BROWSER_DESCRIPTOR = msg({
	message: 'Finish paying in your browser',
	comment: 'Title in the buy dialog while it waits for the payment.',
});
export const PAYMENT_PAGE_OPENED_DESCRIPTOR = msg({
	message:
		'The {paymentProviderName} payment page opened in a new tab. This window updates by itself once the payment is confirmed.',
	comment: 'Body of the buy dialog while it waits for the payment. {paymentProviderName} is the payment provider.',
});
export const PAYMENT_PAGE_LANGUAGE_DESCRIPTOR = msg({
	message: 'The {paymentProviderName} page is in English and shows the amount in AZN: {amount}.',
	comment:
		'Body of the buy dialog while it waits for the payment. {amount} is the charge in Azerbaijani manat, such as 4.76 AZN.',
});
export const WAITING_FOR_PAYMENT_DESCRIPTOR = msg({
	message: 'Waiting for payment…',
	comment: 'Status line in the buy dialog while it waits for the payment.',
});
export const OPEN_PAYMENT_PAGE_DESCRIPTOR = msg({
	message: 'Open the payment page',
	comment: 'Main button in the buy dialog while it waits for the payment; opens the payment page again.',
});
export const ORDER_NUMBER_DESCRIPTOR = msg({
	message: 'Order {orderId}',
	comment: 'Order number in the buy dialog. {orderId} is a long number.',
});
export const PURCHASE_ADDED_ONCE_CONFIRMED_DESCRIPTOR = msg({
	message: 'Once the payment is confirmed, your purchase is added to your account.',
	comment: 'Toast when the user closes the buy dialog while it waits for the payment.',
});
export const EQUIPPED_SUCCESS_DESCRIPTOR = msg({
	message: 'Equipped',
	context: 'shop-success',
	comment: 'Title after a bought or redeemed item has been added and put on.',
});
export const NOW_ON_YOUR_PROFILE_DESCRIPTOR = msg({
	message: '{name} is now on your profile.',
	comment: 'Body after a bought or redeemed item has been added and put on. {name} is the item name.',
});
export const NOW_IN_YOUR_EMOTE_MENU_DESCRIPTOR = msg({
	message: '{name} is now in your emote menu in 3D voice rooms.',
	comment:
		'Body after a bought or redeemed 3D room emote (a body movement such as a dance) has been added. {name} is the emote name.',
});
export const PREMIUM_ACTIVE_DESCRIPTOR = msg({
	message: '{premiumProductName} is active',
	comment: 'Title after buying Plus, and the Plus status line.',
});
export const UNTIL_DATE_DESCRIPTOR = msg({
	message: 'Until {date}',
	comment: 'Body after buying Plus. {date} is the date Plus ends.',
});
export const UNTIL_DATE_DAYS_LEFT_DESCRIPTOR = msg({
	message: 'Until {date}, {days, plural, one {# day} other {# days}} left',
	comment: 'Plus status line on the Plus page. {date} is the date Plus ends.',
});
export const PAYMENT_NOT_COMPLETED_DESCRIPTOR = msg({
	message: 'Payment not completed',
	comment: 'Title in the buy dialog when the payment failed.',
});
export const PAYMENT_NOT_APPROVED_DESCRIPTOR = msg({
	message: 'The payment was not approved. You can try again.',
	comment: 'Body in the buy dialog when the payment was declined, canceled or timed out.',
});
export const ORDER_MISMATCH_DESCRIPTOR = msg({
	message: 'Something is off with this order. If money was taken, contact support with the order number.',
	comment: 'Body in the buy dialog when the paid amount or currency does not match the order.',
});
export const PAYMENT_NOT_CONFIRMED_YET_DESCRIPTOR = msg({
	message: 'Payment not confirmed yet',
	comment: 'Title in the buy dialog when it stopped waiting for the payment.',
});
export const PAYMENT_TIMEOUT_DESCRIPTOR = msg({
	message: 'If you finished paying, your purchase shows up within a few minutes. You can close this window.',
	comment: 'Body in the buy dialog when it stopped waiting for the payment.',
});
export const CHECK_AGAIN_DESCRIPTOR = msg({
	message: 'Check again',
	comment: 'Button in the buy dialog that asks for the payment status again.',
});
export const SALES_PAUSED_DESCRIPTOR = msg({
	message: 'Sales are paused',
	comment: 'Title in the buy dialog when the server refused the order because sales are switched off.',
});
export const CANT_BE_BOUGHT_RIGHT_NOW_DESCRIPTOR = msg({
	message: "This can't be bought right now.",
	comment: 'Body in the buy dialog when the server refused the order because sales are switched off.',
});
export const TOO_MANY_ORDERS_DESCRIPTOR = msg({
	message: 'Too many orders right now',
	comment: 'Title in the buy dialog when the payment provider is busy.',
});
export const TRY_AGAIN_IN_A_FEW_SECONDS_DESCRIPTOR = msg({
	message: 'Try again in a few seconds.',
	comment: 'Body in the buy dialog when the payment provider is busy.',
});
// Existing text (RateLimitedConfirmModal), reused as it is.
export const RATE_LIMITED_WAIT_DESCRIPTOR = msg({
	message: "You're being rate limited. Wait {duration} before trying again.",
	comment:
		'Body in the buy dialog after the daily order limit. {duration} is a localized short duration such as "72 minutes".',
});
export const ALREADY_OWNED_DESCRIPTOR = msg({
	message: 'You already have this item.',
	comment: 'Buy dialog when the user got the item another way meanwhile.',
});
export const CODE_GIVES_ITEM_DESCRIPTOR = msg({
	message: 'This code gives you {name}.',
	comment: 'Gift dialog for a code that gives a decoration. {name} is the item name.',
});
export const ADDED_AND_EQUIPPED_DESCRIPTOR = msg({
	message: 'It is added to your collection and equipped.',
	comment: 'Gift dialog for a code that gives a decoration.',
});
export const REDEEM_AND_EQUIP_DESCRIPTOR = msg({
	message: 'Redeem and equip',
	comment: 'Button in the gift dialog for a code that gives a decoration.',
});
export const ALREADY_OWNED_CODE_KEPT_DESCRIPTOR = msg({
	message: 'You already have this item. The code was not used, so you can give it to someone else.',
	comment: 'Gift dialog for a decoration code when the user already has the item.',
});
export const ADD_TIME_DESCRIPTOR = msg({
	message: 'Add time',
	comment: 'Button on the Plus status line and on the packages for Plus members.',
});
export const PREMIUM_DECORATIONS_DESCRIPTOR = msg({
	message: 'Plus decorations',
	comment: 'Comparison table row on the Plus page: decorations every Plus member can wear.',
});
export const CHOOSE_A_PLAN_DESCRIPTOR = msg({
	message: 'Choose a plan',
	comment: 'Heading of the Plus packages.',
});
export const PREMIUM_SALES_PAUSED_DESCRIPTOR = msg({
	message: '{premiumProductName} sales are paused right now.',
	comment: 'Notice on the Plus page while Plus sales are switched off.',
});
export const REDEEM_KEY_BELOW_DESCRIPTOR = msg({
	message: 'If you have a key, you can redeem it below.',
	comment: 'Notice on the Plus page while Plus sales are switched off.',
});
export const CONFIRMING_PAYMENT_DESCRIPTOR = msg({
	message: 'Confirming your payment',
	comment: 'Title of the payment return page in the browser while the payment is being confirmed.',
});
export const PAYMENT_RETURN_PENDING_DESCRIPTOR = msg({
	message: 'You can close this tab and return to the app. Your purchase appears there once it is confirmed.',
	comment: 'Body of the payment return page in the browser while the payment is being confirmed.',
});

// Micup Visionary: lifetime Plus with a numbered badge, sold on the Plus page.
export const VISIONARY_NAME_DESCRIPTOR = msg({
	message: 'Micup Visionary',
	comment: 'Product name on the Visionary card and buy dialog. A product name: keep it as written.',
});
export const VISIONARY_REWARDS_DESCRIPTOR = msg({
	message: 'Lifetime {premiumProductName}, a numbered badge, the #0000 tag and a decoration only Visionaries have.',
	comment: 'What Visionary gives, on its card and in its buy dialog.',
});
export const SOLD_OUT_DESCRIPTOR = msg({
	message: 'Sold out',
	context: 'visionary',
	comment: 'Button on the Visionary card and buy dialog title when no spot is left.',
});
export const VISIONARY_SOLD_OUT_DESCRIPTOR = msg({
	message: 'The last Visionary spots are gone.',
	comment: 'Body of the Visionary buy dialog when no spot is left.',
});
export const LIFETIME_MEANS_DESCRIPTOR = msg({
	message: 'Lifetime means for as long as Micup runs.',
	comment: 'Note in the Visionary buy dialog, under the currency note.',
});
export const REMAINING_TIME_KEPT_DESCRIPTOR = msg({
	message: 'Your remaining {premiumProductName} time becomes part of the lifetime plan.',
	comment: 'Note in the Visionary buy dialog for a user who still has Plus time.',
});
export const YOU_ARE_VISIONARY_NUMBER_DESCRIPTOR = msg({
	message: 'You are Visionary #{number}',
	comment: 'Title after buying Visionary. {number} is the numbered spot the user got.',
});
export const YOU_ARE_A_VISIONARY_DESCRIPTOR = msg({
	message: 'You are a Visionary',
	comment: 'Title after buying Visionary, before the number is known.',
});
export const VISIONARY_NUMBER_LIFETIME_DESCRIPTOR = msg({
	message: 'Visionary #{number} · Lifetime',
	comment: 'Plus status card of a Visionary. {number} is their numbered spot.',
});
export const BUNDLES_DESCRIPTOR = msg({
	message: 'Bundles',
	comment: 'Heading of the shop section with bundles: several profile decorations sold together for one price.',
});
export const BUNDLE_ITEM_COUNT_DESCRIPTOR = msg({
	message: '{count, plural, one {# item} other {# items}}',
	comment: 'Line on a shop bundle card: how many profile decorations the bundle gives.',
});
export const BUNDLE_INCLUDES_DESCRIPTOR = msg({
	message: 'Includes',
	comment: 'Heading of the list of profile decorations a shop bundle gives, in its buy dialog.',
});
export const BUNDLE_ITEM_OWNED_DESCRIPTOR = msg({
	message: 'You already own an item in this bundle',
	comment:
		'Shop bundle card price line, and title in its buy dialog, when the user has one of its items: the bundle cannot be bought.',
});
export const BUNDLE_ITEMS_YOURS_DESCRIPTOR = msg({
	message: 'The items in {name} are now yours.',
	comment: 'Body after a shop bundle is bought, above the list of its items. {name} is the bundle name.',
});
export const BUNDLE_CONTENTS_CHANGED_DESCRIPTOR = msg({
	message: "This bundle's contents changed",
	comment: 'Title in the bundle buy dialog when the order gives other items than the ones shown; the new list follows.',
});
export const BUNDLE_CARD_TITLE_DESCRIPTOR = msg({
	message: '{count}-piece {name}',
	comment:
		'Title of a shop bundle card, as in "3-piece Angel Wings". {count} is how many profile decorations the bundle gives; {name} is the bundle name.',
});
export const BUY_FOR_PRICE_DESCRIPTOR = msg({
	message: 'Buy for {price}',
	comment: 'Button on a shop card that opens the buy dialog. {price} is the price the user pays, in TL or AZN.',
});
export const BUY_COLLECTION_DESCRIPTOR = msg({
	message: 'Buy the collection',
	comment: 'Button at the top of the shop page that buys the featured set (its bundle) at once.',
});
export const BUY_ALL_IN_SET_DESCRIPTOR = msg({
	message: 'Buy all: {name}',
	comment: 'Button beside a set heading in the shop: buys the set bundle. {name} is the set (bundle) name.',
});
export const TAKE_ME_THERE_DESCRIPTOR = msg({
	message: 'Take me there',
	comment: 'Button on a shop set tile that opens the page of that set.',
});
export const BROWSE_ALL_DESCRIPTOR = msg({
	message: 'Browse all',
	comment: 'First step of the breadcrumb on a shop set page; goes back to the whole shop.',
});
export const SHOP_BREADCRUMB_DESCRIPTOR = msg({
	message: 'Shop breadcrumb',
	comment: 'Accessible name of the breadcrumb at the top of a shop set page.',
});
export const SAVE_VS_MONTHLY_DESCRIPTOR = msg({
	message: 'Save {percent} vs the monthly plan',
	comment:
		'Green line on a Plus package card: what a month of this package saves against the one month package. {percent} is a formatted percentage such as 16%. Not a campaign discount.',
});
export const SHOP_REGION_CLOSED_DESCRIPTOR = msg({
	message:
		"Purchases aren't available in your country or region right now. Items you own can still be equipped, and codes can still be redeemed.",
	comment:
		'Shop, Plus page and buy dialog where no payment provider sells to the country the viewer is in. "Codes" are gift and redeem codes.',
});
export const ACCOUNT_PURCHASES_OFF_DESCRIPTOR = msg({
	message: 'Purchases are turned off for this account.',
	comment: 'Notice on the Plus page when the server has turned purchases off for this account.',
});
export const NOT_SOLD_IN_REGION_DESCRIPTOR = msg({
	message: "Can't be bought in your region",
	comment:
		'Price line of a shop item or bundle whose price is under the smallest amount the payment provider for the viewer country accepts.',
});
export const FINISH_PAYING_IN_WINDOW_DESCRIPTOR = msg({
	message: 'Finish paying in the {paymentProviderName} window',
	comment: "Title in the buy dialog while the payment provider's checkout window is open over the app.",
});
export const PAYMENT_WINDOW_OPEN_DESCRIPTOR = msg({
	message:
		'The {paymentProviderName} payment window is open on this page. This window updates by itself once the payment is confirmed.',
	comment: "Text in the buy dialog while the payment provider's checkout window is open over the app.",
});
export const BUY_BOOST_DESCRIPTOR = msg({
	message: 'Buy a boost',
	comment: 'Title of the buy dialog for one community boost slot, and the button on the boost page that opens it.',
});
export const BOOST_SLOT_NAME_DESCRIPTOR = msg({
	message: 'Boost slot',
	comment: 'Name under the boost gem in the buy dialog: the product is one slot that boosts one community.',
});
export const BOOST_SLOT_NOTE_DESCRIPTOR = msg({
	message: 'One boost slot for 30 days from payment. Put it on any community you are in.',
	comment: 'Note in the buy dialog of a boost slot: what the purchase gives and how long it lasts.',
});
export const BOOST_READY_DESCRIPTOR = msg({
	message: 'Your boost slot is ready',
	comment: 'Title after a boost slot purchase was delivered.',
});
export const BOOST_READY_BODY_DESCRIPTOR = msg({
	message: 'It lasts 30 days from payment.',
	comment: 'Text after a boost slot purchase was delivered.',
});
export const BOOSTS_READY_DESCRIPTOR = msg({
	message: '{count, plural, one {Your boost is ready} other {Your # boosts are ready}}',
	comment: 'Title after a boost subscription was paid and its boost slots were delivered. {count} is how many.',
});
export const BOOSTS_EACH_30_DAYS_DESCRIPTOR = msg({
	message: 'Each lasts 30 days from payment.',
	comment: 'Text after several one-time boost slots were delivered: each one lasts 30 days and does not renew.',
});
export const BOOSTS_RENEW_DESCRIPTOR = msg({
	message: 'They renew every month until you cancel.',
	comment: 'Text after a boost subscription was delivered: the boost slots renew monthly.',
});
export const BOOST_LIMIT_TITLE_DESCRIPTOR = msg({
	message: 'You hold the most bought boosts',
	comment: 'Title in the buy dialog when the server refused a boost order because the account holds its limit.',
});
export const BOOST_LIMIT_DESCRIPTOR = msg({
	message:
		'{count, plural, one {You can hold up to # bought boost at a time.} other {You can hold up to # bought boosts at a time.}}',
	comment:
		'Why a boost slot cannot be bought: the account already holds the most bought boost slots the instance allows. A slot counts until it ends.',
});

// Micup (username card): a 1-3 character username sold in the shop as the name itself.
export const USERNAME_CARDS_DESCRIPTOR = msg({
	message: 'Username cards',
	comment: 'Shop row of username cards: a 1, 2 or 3 character username, sold as the name itself.',
});

export const USERNAME_CARD_TITLE_DESCRIPTOR = msg({
	message: '{length}-character username',
	comment: 'Name of a username card on its shop card and buy dialog. {length} is 1, 2 or 3.',
});

export const PICK_A_FREE_SHORT_NAME_DESCRIPTOR = msg({
	message: 'Pick a free short name and make it yours.',
	comment: 'Username card buy dialog, above the field where the user types the name to buy.',
});

export const USERNAME_CARD_PERMIT_DESCRIPTOR = msg({
	message: 'A permit, not a decoration: it is your username.',
	comment: 'Username card buy dialog, under the card image: the card is not worn, it is the username itself.',
});

export const USERNAME_LABEL_DESCRIPTOR = msg({
	message: 'Username',
	comment: 'Label of the field where the user types the username to buy in the username card buy dialog.',
});

export const USERNAME_CARD_HINT_DESCRIPTOR = msg({
	message: '{length} letters or digits',
	comment: 'Username card buy dialog, under the empty name field. {length} is 1, 2 or 3.',
});

export const USERNAME_CARD_EXACT_LENGTH_DESCRIPTOR = msg({
	message: 'Use exactly {length} letters (a–z) or digits.',
	comment: 'Username card buy dialog: the typed name is too short or has other characters. {length} is 1, 2 or 3.',
});

export const CHECKING_AVAILABILITY_DESCRIPTOR = msg({
	message: 'Checking availability…',
	comment: 'Username card buy dialog, while the typed name is being checked.',
});

export const NAME_IS_FREE_DESCRIPTOR = msg({
	message: 'This name is free.',
	comment: 'Username card buy dialog: the typed name can be bought.',
});

export const NAME_IS_ALREADY_YOURS_DESCRIPTOR = msg({
	message: 'This is already your name.',
	comment: 'Username card buy dialog: the typed name is the account’s own username, so there is nothing to buy.',
});

export const NAME_IS_TAKEN_DESCRIPTOR = msg({
	message: 'This name is taken.',
	comment: 'Username card buy dialog: another account has the typed name, or it was released recently.',
});

export const NAME_IS_HELD_DESCRIPTOR = msg({
	message: 'This name is held for an order right now. Try again in a few minutes.',
	comment: 'Username card buy dialog: an order holds the typed name for up to 6 minutes; it may be the user’s own.',
});

export const NAME_IS_HELD_FOR_YOU_DESCRIPTOR = msg({
	message: 'This name is held for you. Continue to payment.',
	comment: 'Username card buy dialog: the user’s own open order holds the typed name.',
});

export const NAME_CANNOT_BE_USED_DESCRIPTOR = msg({
	message: 'This name cannot be used.',
	comment: 'Username card buy dialog: the name rules or the blocked words list refuse the typed name.',
});

export const USERNAME_CARD_NOT_ON_SALE_DESCRIPTOR = msg({
	message: 'This card is not on sale right now.',
	comment: 'Username card buy dialog: names of this length are not sold right now.',
});

export const NAME_CHECK_FAILED_DESCRIPTOR = msg({
	message: 'Could not check this name. Try again.',
	comment: 'Username card buy dialog: checking the typed name failed.',
});

export const HOLD_NAME_FOR_YOU_DESCRIPTOR = msg({
	message: 'We will hold this name for you for 5 minutes: @{name}',
	comment: 'Username card confirm step, before the order opens. {name} is the username being bought.',
});

export const PAY_WITHIN_5_MINUTES_DESCRIPTOR = msg({
	message: 'Pay within 5 minutes, or the name becomes free again.',
	comment: 'Username card confirm step, under the hold notice.',
});

export const NAME_BECOMES_YOURS_DESCRIPTOR = msg({
	message: 'Once the payment is confirmed, your username becomes this name and your old name is released.',
	comment: 'Username card confirm step.',
});

export const SHORT_NAME_RELEASED_LATER_DESCRIPTOR = msg({
	message: 'If you change your name later, this short name is released; using it again means buying it again.',
	comment: 'Username card confirm step: the bought right ends with the name.',
});

export const HOLD_AND_PAY_DESCRIPTOR = msg({
	message: 'Hold and pay',
	comment: 'Username card confirm step button: opens the order, which holds the name, and goes on to payment.',
});

export const CHANGE_NAME_DESCRIPTOR = msg({
	message: 'Change name',
	comment: 'Username card confirm step button: back to the name field.',
});

export const HELD_FOR_YOU_UNTIL_DESCRIPTOR = msg({
	message: 'Held for you until {time}',
	comment: 'Username card buy dialog while the payment is open. {time} is a local clock time, e.g. 14:35.',
});

export const TAKE_FREE_WITH_VISIONARY_DESCRIPTOR = msg({
	message: 'Take it free with your Visionary right',
	comment:
		'Username card buy dialog: button beside the purchase when the typed name may be taken free with the one free username Visionary gives.',
});

export const TAKE_IT_FREE_DESCRIPTOR = msg({
	message: 'Take it free',
	comment: 'Button that takes a username free with the Visionary username right.',
});

export const GIVING_YOU_NAME_DESCRIPTOR = msg({
	message: 'Giving you @{name}',
	comment:
		'Username card dialog: the name taken free with the Visionary right is on its way to the account. {name} is the username without the @.',
});

export const SETTING_UP_USERNAME_DESCRIPTOR = msg({
	message: 'Setting up your new username…',
	comment: 'Username card dialog, beside a spinner: the free Visionary name is being made the account username.',
});

export const VISIONARY_RIGHT_UNAVAILABLE_DESCRIPTOR = msg({
	message: 'This right has been used or is not valid right now.',
	comment: 'Username card dialog: the server refused taking a name free with the Visionary username right.',
});

export const TAKE_NAME_FREE_TITLE_DESCRIPTOR = msg({
	message: 'Take @{name} free?',
	comment: 'Confirm step before a username is taken free with the Visionary username right. {name} is the username.',
});

export const VISIONARY_RIGHT_ONCE_DESCRIPTOR = msg({
	message: 'Your Visionary right gives you one username, once. It becomes yours right away.',
	comment: 'Confirm step before a username is taken free with the Visionary username right.',
});

export const VISIONARY_NAME_RIGHT_TITLE_DESCRIPTOR = msg({
	message: 'Your Visionary username',
	comment: 'Shop card for a Visionary: one short username they may take free, once.',
});

export const VISIONARY_NAME_RIGHT_BODY_DESCRIPTOR = msg({
	message: 'Visionary gives you one short username free. Pick how long it is.',
	comment: 'Shop card for a Visionary, above the buttons that pick the length of the free username.',
});

export const NAME_LENGTH_OPTION_DESCRIPTOR = msg({
	message: '{length, plural, one {# character} other {# characters}}',
	comment: 'Button on the Visionary username card that opens the free username step for names of this length.',
});

export const SPECIAL_USERNAMES_DESCRIPTOR = msg({
	message: 'Special usernames',
	comment: 'Shop row of single short usernames the shop sells by name, each at its own price, such as @777.',
});

export const SHOW_MORE_USERNAMES_DESCRIPTOR = msg({
	message: 'Show more',
	comment: 'Button under the special usernames row in the shop: loads the next page of names.',
});

export const USERNAME_OFFER_NOT_FOUND_DESCRIPTOR = msg({
	message: 'This offer was not found or has ended.',
	comment:
		'Shown when a personal username offer link is opened by another account, after the offer ended, or with an unknown code.',
});

export const USERNAME_OFFER_UNTIL_DESCRIPTOR = msg({
	message: 'Your personal offer, until {date}',
	comment: 'Buy dialog of a personal username offer from a link. {date} is the date the offer ends.',
});

export const YOU_ARE_NOW_NAME_DESCRIPTOR = msg({
	message: 'You are now @{name}',
	comment: 'Username card bought: the account’s username is now {name}.',
});

// Renewing Plus (Paddle only). Every amount is the full charge for one period, never a monthly equivalent.
export const SUBSCRIBE_DESCRIPTOR = msg({
	message: 'Subscribe',
	comment: 'Button on a Plus package card and in the Plus buy dialog where Plus sells as a renewing subscription.',
});
export const TRY_FREE_FOR_DAYS_DESCRIPTOR = msg({
	message: '{days, plural, one {Try free for # day} other {Try free for # days}}',
	comment:
		'Button on a renewing Plus package card and in the Plus buy dialog when the subscription starts with a free trial. {days} is its length.',
});
export const RENEWS_CANCEL_ANY_TIME_DESCRIPTOR = msg({
	message: 'Renews automatically, cancel any time',
	comment: 'Line on a Plus package card, and fine print under the Plus page buy button, where Plus renews.',
});
export const RECURRING_TOTAL_DESCRIPTOR = msg({
	message: '{amount} every {months, plural, =12 {year} one {month} other {# months}}',
	comment: 'The full charge of a renewing Plus subscription for each period, e.g. "$12.99 every 3 months".',
});
export const RECURRING_RENEWAL_NOTE_DESCRIPTOR = msg({
	message:
		'Renews automatically: {amount} is charged every {months, plural, =12 {year} one {month} other {# months}} until you cancel. Cancel any time in User settings › {premiumProductName}; it takes effect at the end of the paid period.',
	comment:
		'Note in the Plus buy dialog and under the packages for automatic renewal. Legal-effect text: keep the amount, the period, automatic charging and how cancelling works.',
});
export const RECURRING_CONSENT_DESCRIPTOR = msg({
	message:
		'I agree that {premiumProductName} renews automatically and {amount} is charged every {months, plural, =12 {year} one {month} other {# months}} until I cancel.',
	comment: 'Required checkbox in the Plus buy dialog before an automatic renewal order. Legal-effect text.',
});
export const RECURRING_QUOTE_CHANGED_DESCRIPTOR = msg({
	message: 'The price or terms have changed. Check the new amount and tick the box again.',
	comment: 'Shown in the Plus buy dialog when the server refused the renewal terms the buyer agreed to.',
});
export const RECURRING_UNAVAILABLE_DESCRIPTOR = msg({
	message: 'Automatic renewal is not available right now. You can still pay once.',
	comment: 'Shown in the Plus buy dialog when the server offers no automatic renewal.',
});
export const RECURRING_AVAILABLE_AFTER_DESCRIPTOR = msg({
	message: 'Available from {date}. Your current time does not turn into a subscription by itself.',
	comment: 'Shown with why automatic renewal is not offered yet, when it opens later. {date} is a long date.',
});
export const SUBSCRIPTION_BLOCKS_ONE_TIME_DESCRIPTOR = msg({
	message: 'You have a {premiumProductName} subscription. Manage it in User settings › {premiumProductName}.',
	comment:
		'Shown instead of the Plus packages and the buy button while the account has a Plus subscription, renewing or canceled with paid time left.',
});
// Why this account cannot start automatic renewal; the reasons that close it for everyone show nothing.
export const RECURRING_ELIGIBILITY_DESCRIPTORS = {
	prepaid_active: msg({
		message: 'You can subscribe once your current {premiumProductName} time ends.',
		comment: 'Why automatic renewal is not offered: the account still has Plus time bought once.',
	}),
	existing_subscription: msg({
		message: 'You already have a subscription.',
		comment: 'Why automatic renewal is not offered: the account already has a renewing subscription.',
	}),
	checkout_pending: msg({
		message: 'A {premiumProductName} payment is still open. Try again once it is finished or has expired.',
		comment: 'Why automatic renewal is not offered: another Plus payment of the account is not settled yet.',
	}),
	visionary: msg({
		message: 'Visionary already includes {premiumProductName} for life.',
		comment: 'Why automatic renewal is not offered: the account has lifetime Plus.',
	}),
	purchase_disabled: msg({
		message: 'Purchases are turned off for this account.',
		comment: 'Why automatic renewal is not offered: the account may not buy.',
	}),
} as const;
export const RECURRING_PACKAGES_NOTE_DESCRIPTOR = msg({
	message:
		'Renews automatically at the price shown until you cancel. Cancel any time in User settings › {premiumProductName}; it takes effect at the end of the paid period.',
	comment:
		'Note under the Plus packages when automatic renewal is chosen; each card shows its own period total. Legal-effect text.',
});

export const NAME_IS_HELD_UNTIL_DESCRIPTOR = msg({
	message:
		'Someone is trying to buy this username right now. If it isn’t sold within {minutes, plural, one {# minute} other {# minutes}}, it becomes available again.',
	comment:
		'Username card buy dialog: another user’s open order holds the typed name. {minutes} is how many minutes are left of that hold (1 to 6).',
});

export const NAME_IS_HELD_FOR_PAYMENT_DESCRIPTOR = msg({
	message:
		'A payment for this username is being confirmed. If it doesn’t go through, the name becomes available again.',
	comment:
		'Username card buy dialog: an order’s payment for the typed name is waiting for the payment provider’s confirmation.',
});
